import React, { useState, useEffect, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronRight, ChevronLeft } from "lucide-react";

/**
 * Dual-list column picker ("Select Columns to Display") — shared by the Leads,
 * Filters and Appointments tables.
 *
 * Give it the page's full column list (`columns`: [{ key, label, locked? }])
 * and the keys currently shown; it hands back the chosen keys IN ORDER via
 * `onApply`. Order matters — the in-use list is drag-to-reorder.
 *
 * A column with `locked: true` is always in use: it never appears in the
 * Available list, can't be checked, and can't be dragged. That's how each page
 * pins its identity column (Phone on Leads, Name on Appointments).
 */
export default function ColumnChooserDialog({ open, onOpenChange, columns, visibleCols, onApply }) {
  const [inUse, setInUse] = useState([]);
  const [availSelected, setAvailSelected] = useState(new Set());
  const [inUseSelected, setInUseSelected] = useState(new Set());
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  const colMap = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c])), [columns]);

  useEffect(() => { if (open) { setInUse([...visibleCols]); setAvailSelected(new Set()); setInUseSelected(new Set()); } }, [open, visibleCols]);

  const available = columns.filter((c) => !c.locked && !inUse.includes(c.key));

  const moveRight = () => {
    if (availSelected.size === 0) return;
    setInUse((prev) => [...prev, ...columns.filter((c) => availSelected.has(c.key)).map((c) => c.key)]);
    setAvailSelected(new Set());
  };
  const moveLeft = () => {
    if (inUseSelected.size === 0) return;
    setInUse((prev) => prev.filter((k) => !inUseSelected.has(k)));
    setInUseSelected(new Set());
  };

  const toggleAvail = (key) => setAvailSelected((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleInUse = (key) => { if (colMap[key]?.locked) return; setInUseSelected((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; }); };

  const handleDragEnd = () => {
    if (dragItem.current == null || dragOver.current == null || dragItem.current === dragOver.current) { dragItem.current = null; dragOver.current = null; return; }
    setInUse((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragItem.current, 1);
      next.splice(dragOver.current, 0, moved);
      return next;
    });
    dragItem.current = null;
    dragOver.current = null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Select Columns to Display</DialogTitle>
          <p className="text-sm text-muted-foreground">Choose and move columns to display in the table.</p>
        </DialogHeader>
        <div className="flex gap-2 items-stretch flex-1 min-h-0 h-[400px]">
          {/* Available */}
          <div className="flex-1 border rounded-lg overflow-hidden flex flex-col">
            <div className="px-3 py-2 bg-cream/50 border-b text-xs font-semibold text-muted-foreground flex items-center justify-between">
              <span>{availSelected.size}/{available.length} Items</span>
              <span>Columns Available</span>
            </div>
            <div className="flex-1 overflow-y-auto p-1">
              {available.map((c) => (
                <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-accent rounded">
                  <Checkbox checked={availSelected.has(c.key)} onCheckedChange={() => toggleAvail(c.key)} className="h-3.5 w-3.5" />
                  {c.label}
                </label>
              ))}
              {available.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">All columns in use</p>}
            </div>
          </div>
          {/* Move buttons */}
          <div className="flex flex-col items-center justify-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={moveRight} disabled={availSelected.size === 0}><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={moveLeft} disabled={inUseSelected.size === 0}><ChevronLeft className="h-4 w-4" /></Button>
          </div>
          {/* In Use */}
          <div className="flex-1 border rounded-lg overflow-hidden flex flex-col">
            <div className="px-3 py-2 bg-cream/50 border-b text-xs font-semibold text-muted-foreground flex items-center justify-between">
              <span>{inUse.length} Items</span>
              <span>Columns in Use</span>
            </div>
            <div className="flex-1 overflow-y-auto p-1">
              {inUse.map((key, idx) => {
                const col = colMap[key];
                if (!col) return null;
                const locked = col.locked;
                return (
                  <div
                    key={key}
                    draggable={!locked}
                    onDragStart={() => { dragItem.current = idx; }}
                    onDragEnter={() => { dragOver.current = idx; }}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                    className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded select-none ${locked ? "opacity-60" : "cursor-grab hover:bg-accent active:cursor-grabbing"}`}
                  >
                    <Checkbox checked={inUseSelected.has(key)} onCheckedChange={() => toggleInUse(key)} className="h-3.5 w-3.5" disabled={locked} />
                    <span className="flex-1">{col.label}</span>
                    {!locked && <span className="text-muted-foreground/50 cursor-grab" title="Drag to reorder">≡</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onApply(inUse); onOpenChange(false); }} className="bg-navy text-white hover:bg-navy-hover">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
